import WarehouseOutlined from "@mui/icons-material/WarehouseOutlined";
import { Typography, useTheme } from "@mui/material";
import { Grid } from "@mui/system";
import { DisplayFieldWithTooltip } from "components/DisplayFieldWithTooltip";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { formatAddress } from "encamp-shared/src/utils/address";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { WasteManifestSource } from "generated-graphql/graphql";
import { getDaysInTransitInfo } from "util/manifest";

import { FieldSourceValues } from "../useManifestDiscrepancyFields";
import { ManifestPartyCard } from "./ManifestPartyCard";
import { SourcedField } from "./SourcedField";
import { ManifestDetail } from "./types";

interface ReceivingTsdfSectionProps {
  manifest: ManifestDetail;
  source: WasteManifestSource;
  availableSources: readonly WasteManifestSource[];
  valuesByField: Map<string, FieldSourceValues>;
  /** UI field keys with an open cross-source discrepancy — drives warning icons. */
  disagreementFields: Set<string>;
  /**
   * Pre-summarized management method across the manifest's lines: a single
   * "code · name" when every line agrees, "Multiple (N)" when they differ, or
   * undefined when no line carries a code (the field is then omitted).
   */
  managementMethod?: string;
  /** Opens the full compare dialog from a field's provenance popover (staff only). */
  onOpenCompare?: () => void;
  /**
   * True when the card sits in the 3-up Generator → Transporters → Receiving
   * row: fields stack in a single column so they fit the ~1/3-width card.
   */
  narrow?: boolean;
}

/**
 * Receiving (TSDF) card: destination name/EPA/address vary by source (vendor vs
 * e-Manifest); the received date and days-in-transit come from the e-Manifest.
 */
export function ReceivingTsdfSection({
  manifest,
  source,
  availableSources,
  valuesByField,
  disagreementFields,
  managementMethod,
  onOpenCompare,
  narrow = false,
}: ReceivingTsdfSectionProps) {
  const theme = useTheme();
  // Half-width at sm+, except in the narrow 3-up row where md+ is the squeezed
  // side-by-side layout (below md the cards stack full-width again).
  const fieldSize = { xs: 12, sm: 6, md: narrow ? 12 : 6 };
  const daysInTransitFields = getDaysInTransitInfo(
    manifest.eManifestDaysInTransit,
    manifest.receivedDate,
    theme
  );

  return (
    <ManifestPartyCard title="Receiving (TSDF)" icon={<WarehouseOutlined fontSize="small" />}>
      <Grid container spacing={2}>
        <Grid size={fieldSize}>
          <SourcedField
            label="TSDF"
            source={source}
            availableSources={availableSources}
            values={{
              [WasteManifestSource.EManifest]: manifest.eManifest?.desFacilityName,
              [WasteManifestSource.Vendor]: manifest.vendorManifest?.desFacilityName,
              [WasteManifestSource.ManifestDocument]: manifest.documentManifest?.desFacilityName,
            }}
            discrepancyValues={valuesByField.get("destinationFacilityName")}
            hasDisagreement={disagreementFields.has("destinationFacilityName")}
            onOpenCompare={onOpenCompare}
          />
        </Grid>
        <Grid size={fieldSize}>
          <SourcedField
            label="TSDF EPA ID"
            source={source}
            availableSources={availableSources}
            values={{
              [WasteManifestSource.EManifest]: manifest.eManifest?.desFacilityId,
              [WasteManifestSource.Vendor]: manifest.vendorManifest?.desFacilityId,
              [WasteManifestSource.ManifestDocument]: manifest.documentManifest?.desFacilityId,
            }}
            discrepancyValues={valuesByField.get("destinationFacilityId")}
            hasDisagreement={disagreementFields.has("destinationFacilityId")}
            onOpenCompare={onOpenCompare}
          />
        </Grid>
        <Grid size={12}>
          <SourcedField
            label="TSDF Address"
            source={source}
            availableSources={availableSources}
            values={{
              [WasteManifestSource.EManifest]: formatAddress(
                manifest.eManifest?.desFacLocationStreet1,
                manifest.eManifest?.desFacLocationCity,
                manifest.eManifest?.desFacLocationState,
                manifest.eManifest?.desFacLocationZip
              ),
              [WasteManifestSource.Vendor]: formatAddress(
                manifest.vendorManifest?.desFacLocationStreet1,
                manifest.vendorManifest?.desFacLocationCity,
                manifest.vendorManifest?.desFacLocationState,
                manifest.vendorManifest?.desFacLocationZip
              ),
              [WasteManifestSource.ManifestDocument]: formatAddress(
                manifest.documentManifest?.desFacLocationStreet1,
                manifest.documentManifest?.desFacLocationCity,
                manifest.documentManifest?.desFacLocationState,
                manifest.documentManifest?.desFacLocationZip
              ),
            }}
            onOpenCompare={onOpenCompare}
          />
        </Grid>
        {manifest.isRegulatedEManifest && (
          <Grid size={fieldSize}>
            <DisplayOnlyField
              label="Received by TSDF"
              copyable={false}
              text={manifest.receivedDate ? getUtcDisplayDate(manifest.receivedDate) : undefined}
              renderValue={(value) =>
                value ? (
                  value
                ) : (
                  <Typography fontStyle="italic" color="text.secondary" variant="body2">
                    Pending
                  </Typography>
                )
              }
            />
          </Grid>
        )}
        {daysInTransitFields && (
          <Grid size={fieldSize}>
            <DisplayFieldWithTooltip
              text={daysInTransitFields.display}
              textColor={daysInTransitFields.color}
              isItalic={!manifest.receivedDate}
              label="Days In Transit"
              tooltip={daysInTransitFields.tooltipText}
              tooltipEnterDelay={500}
              tooltipPlacement="right"
              transparentBackground
            />
          </Grid>
        )}
        {managementMethod && (
          <Grid size={fieldSize}>
            <DisplayOnlyField label="Management Method" copyable={false} text={managementMethod} />
          </Grid>
        )}
      </Grid>
    </ManifestPartyCard>
  );
}
